import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  AlertCircle,
  CheckCircle2,
  Users,
  Zap,
  TrendingUp,
  TrendingDown,
  Minus,
  Bug,
  Settings,
  Search,
  Loader2,
  Database,
  GitMerge,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  entityQAService,
  type QADashboardSummary,
  type QAMetricDataPoint,
  type QAIssue,
  type CanonicalEntity,
  type CorrectionRule,
  type Neo4jStatsResponse,
  type RebuildGraphResponse,
  type AIAnalysisResponse,
  type VectorHealthAudit,
  type StaleDocumentsResponse,
  type DuplicateCluster,
} from '@/services/kbService';

// Import reusable components
import { QAPageHeader } from '@/components/entity-qa/QAPageHeader';
import { QAStatCard } from '@/components/entity-qa/QAStatCard';
import { QAErrorAlert } from '@/components/entity-qa/QAErrorAlert';
import { QAIssuesTab } from '@/components/entity-qa/QAIssuesTab';
import { QACanonicalTab } from '@/components/entity-qa/QACanonicalTab';
import { QARulesTab } from '@/components/entity-qa/QARulesTab';
import { QAResolveModal, type ResolveAction } from '@/components/entity-qa/QAResolveModal';
import { QAEditEntityModal } from '@/components/entity-qa/QAEditEntityModal';
import { QARebuildResultModal } from '@/components/entity-qa/QARebuildResultModal';
import { QAAIAnalysisModal } from '@/components/entity-qa/QAAIAnalysisModal';
import { QAVectorHealthTab } from '@/components/entity-qa/QAVectorHealthTab';
import { QADuplicateClustersTab } from '@/components/entity-qa/QADuplicateClustersTab';
import { QASettingsTab } from '@/components/entity-qa/QASettingsTab';
import { logger } from '@/utils/logger';
import { getFriendlyErrorMessage } from '@/utils/errorMessage';
import {
  ensureArray,
  safeObjectEntries,
  safeObjectKeys,
  type ActiveTab,
} from '@/components/entity-qa/qaUtils';


// ============================================================================
// Helper Functions
// ============================================================================

const getQualityScoreStatus = (score: number): { color: string; icon: React.ReactNode } => {
  if (score >= 90) return { color: 'text-emerald-400', icon: <TrendingUp className="h-4 w-4 sm:h-5 sm:w-5" /> };
  if (score >= 70) return { color: 'text-amber-400', icon: <Minus className="h-4 w-4 sm:h-5 sm:w-5" /> };
  return { color: 'text-red-400', icon: <TrendingDown className="h-4 w-4 sm:h-5 sm:w-5" /> };
};

// ============================================================================
// Main Component
// ============================================================================

const KBEntityQAPage: React.FC = () => {
  // Loading states
  const [dashboardLoading, setDashboardLoading] = useState(true);
  const [metricsLoading, setMetricsLoading] = useState(true);
  const [issuesLoading, setIssuesLoading] = useState(true);
  const [issuesRefreshing, setIssuesRefreshing] = useState(false);
  const [canonicalLoading, setCanonicalLoading] = useState(false);
  const [rulesLoading, setRulesLoading] = useState(false);

  // Error states
  const [dashboardError, setDashboardError] = useState<string | null>(null);
  const [metricsError, setMetricsError] = useState<string | null>(null);
  const [issuesError, setIssuesError] = useState<string | null>(null);
  const [canonicalError, setCanonicalError] = useState<string | null>(null);
  const [rulesError, setRulesError] = useState<string | null>(null);

  // Data states
  const [dashboard, setDashboard] = useState<QADashboardSummary | null>(null);
  const [metrics, setMetrics] = useState<QAMetricDataPoint[]>([]);
  const [issues, setIssues] = useState<QAIssue[]>([]);
  const [canonicalEntities, setCanonicalEntities] = useState<CanonicalEntity[]>([]);
  const [correctionRules, setCorrectionRules] = useState<CorrectionRule[]>([]);

  // Filters
  const [issueStatusFilter, setIssueStatusFilter] = useState<string>('pending');
  const [issueSeverityFilter, setIssueSeverityFilter] = useState<string>('all');
  const [issueTypeFilter, setIssueTypeFilter] = useState<string>('all');
  const [issueSearch, setIssueSearch] = useState<string>('');
  const [canonicalTypeFilter, setCanonicalTypeFilter] = useState<string>('all');
  const [canonicalSearch, setCanonicalSearch] = useState<string>('');

  // Issue type breakdown for distribution badges
  const [issueTypeBreakdown, setIssueTypeBreakdown] = useState<[string, number][]>([]);

  // Issue selection for bulk operations
  const [selectedIssueIds, setSelectedIssueIds] = useState<Set<string>>(new Set());
  const [bulkActioning, setBulkActioning] = useState(false);

  // Pagination
  const [issuesPagination, setIssuesPagination] = useState({ current: 1, pageSize: 50 });
  const [canonicalPagination, setCanonicalPagination] = useState({ current: 1, pageSize: 100 });
  const [issuesTotalCount, setIssuesTotalCount] = useState(0);
  const [canonicalTotalCount, setCanonicalTotalCount] = useState(0);

  // Sort state for canonical entities
  const [canonicalSortBy, setCanonicalSortBy] = useState<string>('canonical_text');
  const [canonicalSortOrder, setCanonicalSortOrder] = useState<'asc' | 'desc'>('asc');

  // Sort state for issues
  const [issueSortBy, setIssueSortBy] = useState<string>('creation');
  const [issueSortOrder, setIssueSortOrder] = useState<'asc' | 'desc'>('desc');

  // Resolve Issue Modal state
  const [resolveModalVisible, setResolveModalVisible] = useState(false);
  const [selectedIssue, setSelectedIssue] = useState<QAIssue | null>(null);
  const [resolveAction, setResolveAction] = useState<ResolveAction>('accept');
  const [resolveNotes, setResolveNotes] = useState('');
  const [resolveCorrectedType, setResolveCorrectedType] = useState('');
  const [resolving, setResolving] = useState(false);
  const [applyToSimilar, setApplyToSimilar] = useState(false);

  // Edit Entity Modal state
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [selectedEntity, setSelectedEntity] = useState<CanonicalEntity | null>(null);
  const [editEntityType, setEditEntityType] = useState<string>('');
  const [editConfidenceScore, setEditConfidenceScore] = useState<number>(0);
  const [editNotes, setEditNotes] = useState<string>('');
  const [editCanonicalValue, setEditCanonicalValue] = useState<string>('');
  const [editSaving, setEditSaving] = useState(false);

  // Bulk selection state
  const [selectedEntityIds, setSelectedEntityIds] = useState<Set<string>>(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);

  // Bulk Retype state
  const [bulkRetypeModalVisible, setBulkRetypeModalVisible] = useState(false);
  const [bulkRetypeTargetType, setBulkRetypeTargetType] = useState<string>('');
  const [bulkRetyping, setBulkRetyping] = useState(false);

  // Rebuild Graph state
  const [rebuildingGraph, setRebuildingGraph] = useState(false);
  const [neo4jStats, setNeo4jStats] = useState<Neo4jStatsResponse | null>(null);
  const [rebuildResultVisible, setRebuildResultVisible] = useState(false);
  const [rebuildResult, setRebuildResult] = useState<RebuildGraphResponse | null>(null);

  // AI Analysis state
  const [aiAnalyzingMode, setAiAnalyzingMode] = useState<'analyze' | 'heal' | null>(null);
  const [aiResultVisible, setAiResultVisible] = useState(false);
  const [aiResult, setAiResult] = useState<AIAnalysisResponse | null>(null);

  // Data Quality Review state
  const [scanning, setScanning] = useState(false);

  // Auto-Fix state
  const [autoFixing, setAutoFixing] = useState(false);
  const [autoFixPreview, setAutoFixPreview] = useState<{
    noise_deleted: number; duplicates_merged: number; orphans_dismissed: number; total_fixed: number;
  } | null>(null);
  const [autoFixConfirmVisible, setAutoFixConfirmVisible] = useState(false);
  const [autoFixByTypeConfirm, setAutoFixByTypeConfirm] = useState<{
    issueType: string; action: string; message: string;
  } | null>(null);

  // Vector Health state
  const [vectorHealthAudit, setVectorHealthAudit] = useState<VectorHealthAudit | null>(null);
  const [vectorStaleData, setVectorStaleData] = useState<StaleDocumentsResponse | null>(null);
  const [vectorHealthLoading, setVectorHealthLoading] = useState(false);
  const [vectorHealthError, setVectorHealthError] = useState<string | null>(null);
  const [reindexing, setReindexing] = useState(false);

  // Duplicate Clusters state
  const [clusters, setClusters] = useState<DuplicateCluster[]>([]);
  const [clustersLoading, setClustersLoading] = useState(false);
  const [clustersError, setClustersError] = useState<string | null>(null);
  const [clustersTotalCount, setClustersTotalCount] = useState(0);
  const [clustersDuplicateCount, setClustersDuplicateCount] = useState(0);
  const [validEntityTypes, setValidEntityTypes] = useState<string[]>([]);

  // Active tab
  const [activeTab, setActiveTab] = useState<ActiveTab>('issues');

  // ============================================================================
  // Data Fetching
  // ============================================================================

  const loadDashboard = useCallback(async () => {
    setDashboardLoading(true);
    setDashboardError(null);
    try {
      const data = await entityQAService.getDashboard();
      if (data && typeof data === 'object') {
        setDashboard(data);
        if (data.total_duplicate_clusters != null) {
          setClustersTotalCount(data.total_duplicate_clusters);
        }
      } else {
        logger.warn('Invalid dashboard response:', data);
        setDashboardError('Invalid dashboard response');
      }
    } catch (error: any) {
      logger.error('Failed to load dashboard:', error);
      setDashboardError(error.message || 'Failed to load dashboard');
      toast.error('Failed to load dashboard data');
    } finally {
      setDashboardLoading(false);
    }
  }, []);

  const loadMetrics = useCallback(async () => {
    setMetricsLoading(true);
    setMetricsError(null);
    try {
      const data = await entityQAService.getMetrics(7);
      const safeData = ensureArray<QAMetricDataPoint>(data);
      setMetrics(safeData);
    } catch (error: any) {
      logger.error('Failed to load metrics:', error);
      setMetricsError(error.message || 'Failed to load metrics');
      setMetrics([]);
    } finally {
      setMetricsLoading(false);
    }
  }, []);

  const loadIssues = useCallback(async () => {
    setIssuesLoading(true);
    setIssuesError(null);
    try {
      const offset = (issuesPagination.current - 1) * issuesPagination.pageSize;
      const params: Record<string, any> = {
        status: issueStatusFilter !== 'all' ? issueStatusFilter : undefined,
        severity: issueSeverityFilter !== 'all' ? issueSeverityFilter : undefined,
        issue_type: issueTypeFilter !== 'all' ? issueTypeFilter : undefined,
        search: issueSearch || undefined,
        sort_by: issueSortBy || undefined,
        sort_order: issueSortOrder || undefined,
        limit: issuesPagination.pageSize,
        offset,
      };
      const data = await entityQAService.getIssues(params);
      setIssues(data.issues);
      setIssuesTotalCount(data.total_count);
      setSelectedIssueIds(new Set());
    } catch (error: any) {
      logger.error('Failed to load issues:', error);
      setIssuesError(error.message || 'Failed to load issues');
      setIssues([]);
      setIssuesTotalCount(0);
    } finally {
      setIssuesLoading(false);
    }
  }, [issueStatusFilter, issueSeverityFilter, issueTypeFilter, issueSearch, issuesPagination, issueSortBy, issueSortOrder]);

  const loadIssueTypeBreakdown = useCallback(async () => {
    try {
      const data = await entityQAService.getIssueTypeBreakdown();
      if (data && data.breakdown) {
        setIssueTypeBreakdown(Object.entries(data.breakdown).map(([k, v]) => [k, v as number]));
      }
    } catch (error: any) {
      logger.error('Failed to load issue type breakdown:', error);
    }
  }, []);

  const loadCanonicalEntities = useCallback(async () => {
    setCanonicalLoading(true);
    setCanonicalError(null);
    try {
      const offset = (canonicalPagination.current - 1) * canonicalPagination.pageSize;
      const params: Record<string, any> = {
        entity_type: canonicalTypeFilter !== 'all' ? canonicalTypeFilter : undefined,
        search: canonicalSearch || undefined,
        limit: canonicalPagination.pageSize,
        offset,
        sort_by: canonicalSortBy,
        sort_order: canonicalSortOrder,
      };
      const data = await entityQAService.getCanonicalEntities(params);
      setCanonicalEntities(data.entities);
      setCanonicalTotalCount(data.total_count);
      // Selection is NOT cleared here -- it persists across page navigation.
      // Selections are cleared explicitly by filter/search/sort change handlers.
    } catch (error: any) {
      logger.error('Failed to load canonical entities:', error);
      setCanonicalError(error.message || 'Failed to load canonical entities');
      setCanonicalEntities([]);
      setCanonicalTotalCount(0);
      setSelectedEntityIds(new Set());
    } finally {
      setCanonicalLoading(false);
    }
  }, [canonicalTypeFilter, canonicalSearch, canonicalPagination, canonicalSortBy, canonicalSortOrder]);

  const loadCorrectionRules = useCallback(async () => {
    setRulesLoading(true);
    setRulesError(null);
    try {
      const data = await entityQAService.getCorrectionRules();
      const safeData = ensureArray<CorrectionRule>(data);
      setCorrectionRules(safeData);
    } catch (error: any) {
      logger.error('Failed to load correction rules:', error);
      setRulesError(error.message || 'Failed to load correction rules');
      setCorrectionRules([]);
    } finally {
      setRulesLoading(false);
    }
  }, []);

  const loadNeo4jStats = useCallback(async () => {
    try {
      const stats = await entityQAService.getNeo4jStats();
      setNeo4jStats(stats);
    } catch (error: any) {
      logger.error('Failed to load Neo4j stats:', error);
      setNeo4jStats(null);
    }
  }, []);

  const loadVectorHealth = useCallback(async () => {
    setVectorHealthLoading(true);
    setVectorHealthError(null);
    try {
      const [auditData, staleData] = await Promise.all([
        entityQAService.auditVectorHealth(100),
        entityQAService.getStaleDocuments(),
      ]);
      setVectorHealthAudit(auditData);
      setVectorStaleData(staleData);
    } catch (error: any) {
      logger.error('Failed to load vector health:', error);
      setVectorHealthError(error.message || 'Failed to load vector health');
    } finally {
      setVectorHealthLoading(false);
    }
  }, []);

  const loadDuplicateClusters = useCallback(async () => {
    setClustersLoading(true);
    setClustersError(null);
    try {
      const data = await entityQAService.getDuplicateClusters();
      setClusters(data.clusters || []);
      setClustersTotalCount(data.total_clusters || 0);
      setClustersDuplicateCount(data.total_duplicate_entities || 0);
    } catch (error: any) {
      logger.error('Failed to load duplicate clusters:', error);
      setClustersError(error.message || 'Failed to load duplicate clusters');
    } finally {
      setClustersLoading(false);
    }
  }, []);

  const loadValidEntityTypes = useCallback(async () => {
    try {
      const types = await entityQAService.getValidEntityTypes();
      setValidEntityTypes(types);
    } catch (error: any) {
      logger.error('Failed to load valid entity types:', error);
    }
  }, []);

  // Initial load (loadIssues is handled by its own useEffect below)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    loadDashboard();
    loadMetrics();
    loadNeo4jStats();
    loadIssueTypeBreakdown();
    loadValidEntityTypes();
  }, []);

  // Load tab-specific data
  useEffect(() => {
    if (activeTab === 'canonical' && canonicalEntities.length === 0 && !canonicalError) {
      loadCanonicalEntities();
    } else if (activeTab === 'clusters' && clusters.length === 0 && !clustersError) {
      loadDuplicateClusters();
      loadValidEntityTypes();
    } else if (activeTab === 'rules' && correctionRules.length === 0 && !rulesError) {
      loadCorrectionRules();
    } else if (activeTab === 'vector-health' && !vectorHealthAudit && !vectorHealthError) {
      loadVectorHealth();
    }
  }, [activeTab, canonicalEntities.length, correctionRules.length, canonicalError, rulesError, loadCanonicalEntities, loadCorrectionRules, vectorHealthAudit, vectorHealthError, loadVectorHealth, clusters.length, clustersError, loadDuplicateClusters, loadValidEntityTypes]);

  // Reload issues when filters or pagination change
  useEffect(() => {
    loadIssues();
  }, [loadIssues]);

  // Reload canonical entities when filter, search, or pagination changes
  // (initial load is handled by the tab-specific useEffect above)
  const canonicalLoadedOnce = useRef(false);
  useEffect(() => {
    if (canonicalLoadedOnce.current) {
      loadCanonicalEntities();
    } else {
      canonicalLoadedOnce.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadCanonicalEntities]);

  // Clear search when switching away from canonical tab
  useEffect(() => {
    if (activeTab !== 'canonical') {
      setCanonicalSearch('');
    }
  }, [activeTab]);

  // ============================================================================
  // Handlers
  // ============================================================================

  const handleRefreshAll = () => {
    loadDashboard();
    loadMetrics();
    loadIssues();
    loadNeo4jStats();
    loadIssueTypeBreakdown();
    if (activeTab === 'canonical') loadCanonicalEntities();
    if (activeTab === 'clusters') loadDuplicateClusters();
    if (activeTab === 'rules') loadCorrectionRules();
    if (activeTab === 'vector-health') loadVectorHealth();
  };

  const handleOpenResolveModal = (issue: QAIssue) => {
    setSelectedIssue(issue);
    setResolveAction('accept');
    setResolveNotes('');
    setResolveModalVisible(true);
  };

  const handleResolveIssue = async () => {
    if (!selectedIssue) return;

    setResolving(true);
    try {
      if (applyToSimilar && similarIssueIds.length > 0) {
        // Resolve the selected issue first
        await entityQAService.resolveIssue(selectedIssue.id, {
          action: resolveAction,
          corrected_type: resolveCorrectedType || undefined,
          notes: resolveNotes || undefined,
        });
        // Bulk resolve all similar issues with the same action
        await entityQAService.bulkResolveIssues(similarIssueIds, resolveAction);
        toast.success(`Resolved ${similarIssueIds.length + 1} similar issues`);
      } else {
        await entityQAService.resolveIssue(selectedIssue.id, {
          action: resolveAction,
          corrected_type: resolveCorrectedType || undefined,
          notes: resolveNotes || undefined,
        });
        toast.success('Issue resolved successfully');
      }
      setResolveModalVisible(false);
      loadIssues();
      loadDashboard();
      loadIssueTypeBreakdown();
    } catch (error: any) {
      logger.error('Failed to resolve issue:', error);
      toast.error(error.response?.data?.detail || 'Failed to resolve issue');
    } finally {
      setResolving(false);
    }
  };

  // Issue Search/Filter handlers
  const handleIssueSearchChange = useCallback((search: string) => {
    setIssueSearch(search);
    setIssuesPagination(p => ({ ...p, current: 1 }));
  }, []);

  const handleIssueTypeFilterChange = useCallback((val: string) => {
    setIssueTypeFilter(val);
    setIssuesPagination(p => ({ ...p, current: 1 }));
  }, []);

  // Issue selection for bulk operations
  const handleSelectIssue = (issueId: string, checked: boolean) => {
    setSelectedIssueIds((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(issueId);
      } else {
        next.delete(issueId);
      }
      return next;
    });
  };

  const handleSelectAllIssues = (checked: boolean) => {
    if (checked) {
      setSelectedIssueIds(new Set(issues.map((i) => i.id)));
    } else {
      setSelectedIssueIds(new Set());
    }
  };

  // Bulk Dismiss Handler
  const handleBulkDismiss = async () => {
    if (selectedIssueIds.size === 0) return;
    setBulkActioning(true);
    try {
      const result = await entityQAService.bulkDismissIssues(Array.from(selectedIssueIds));
      if (result.success) {
        toast.success(`Dismissed ${result.dismissed_count} issues`);
        if (result.failed_count > 0) {
          toast.warning(`Failed to dismiss ${result.failed_count} issues`);
        }
      } else {
        toast.error('Bulk dismiss failed');
      }
      setSelectedIssueIds(new Set());
      loadIssues();
      loadDashboard();
      loadIssueTypeBreakdown();
    } catch (error: any) {
      logger.error('Failed to bulk dismiss:', error);
      toast.error('Failed to bulk dismiss issues');
    } finally {
      setBulkActioning(false);
    }
  };

  // Bulk Delete (for noise/invalid issues)
  const handleBulkDeleteIssues = async () => {
    if (selectedIssueIds.size === 0) return;
    setBulkActioning(true);
    try {
      const result = await entityQAService.deleteQaIssues(Array.from(selectedIssueIds));
      if (result.success) {
        const deletedCount = (result as any).deleted ?? result.deleted_count ?? 0;
        const failedCount = Array.isArray((result as any).failed) ? (result as any).failed.length : (result as any).failed_count ?? 0;
        toast.success(`Deleted ${deletedCount} issues`);
        if (failedCount > 0) {
          toast.warning(`Failed to delete ${failedCount} issues`);
        }
      } else {
        toast.error('Bulk delete failed');
      }
      setSelectedIssueIds(new Set());
      loadIssues();
      loadDashboard();
      loadIssueTypeBreakdown();
    } catch (error: any) {
      logger.error('Failed to bulk delete:', error);
      toast.error('Failed to bulk delete issues');
    } finally {
      setBulkActioning(false);
    }
  };

  // Single-issue dismiss handler
  const handleDismissSingle = async (issueId: string) => {
    try {
      const result = await entityQAService.bulkDismissIssues([issueId]);
      if (result.success) {
        toast.success('Issue dismissed');
        loadIssues();
        loadDashboard();
        loadIssueTypeBreakdown();
      }
    } catch (error: any) {
      logger.error('Failed to dismiss issue:', error);
      toast.error('Failed to dismiss issue');
    }
  };

  // Single-issue delete handler
  const handleDeleteSingle = async (issueId: string) => {
    try {
      const result = await entityQAService.deleteQaIssues([issueId]);
      if (result.success) {
        toast.success('Issue deleted');
        loadIssues();
        loadDashboard();
        loadIssueTypeBreakdown();
      }
    } catch (error: any) {
      logger.error('Failed to delete issue:', error);
      toast.error('Failed to delete issue');
    }
  };

  // Edit Entity Handlers
  const handleOpenEditModal = (entity: CanonicalEntity) => {
    setSelectedEntity(entity);
    setEditEntityType(entity.entity_type || '');
    setEditConfidenceScore(Math.round((entity.confidence_score || 0) * 100));
    setEditCanonicalValue(entity.canonical_value || '');
    setEditNotes(entity.notes || '');
    setEditModalVisible(true);
  };

  const handleSaveEntity = async () => {
    if (!selectedEntity) return;

    setEditSaving(true);
    try {
      const updateData: Record<string, any> = {
        entity_type: editEntityType,
        confidence_score: editConfidenceScore / 100,
        notes: editNotes || undefined,
      };
      if (editCanonicalValue && editCanonicalValue !== selectedEntity.canonical_value) {
        updateData.canonical_value = editCanonicalValue;
      }

      await entityQAService.updateEntity(selectedEntity.id, updateData);
      toast.success('Entity updated successfully');
      setEditModalVisible(false);
      loadCanonicalEntities();
      loadDashboard();
    } catch (error: any) {
      logger.error('Failed to update entity:', error);
      toast.error(error.response?.data?.detail || 'Failed to update entity');
    } finally {
      setEditSaving(false);
    }
  };

  // Delete Entity Handler
  const handleDeleteEntity = async (entityId: string) => {
    try {
      await entityQAService.deleteEntity(entityId);
      toast.success('Entity deleted successfully');
      // Remove deleted entity from selection if selected
      setSelectedEntityIds((prev) => {
        const next = new Set(prev);
        next.delete(entityId);
        return next;
      });
      loadCanonicalEntities();
      loadDashboard();
    } catch (error: any) {
      logger.error('Failed to delete entity:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete entity');
    }
  };

  // Bulk Delete Handler
  const handleBulkDelete = async () => {
    if (selectedEntityIds.size === 0) return;

    setBulkDeleting(true);
    try {
      const result = await entityQAService.bulkDeleteEntities(Array.from(selectedEntityIds));
      if (result.success) {
        toast.success(`Successfully deleted ${result.deleted ?? result.deleted_count ?? 0} entities`);
        if ((result.failed ?? result.failed_count ?? 0) > 0) {
          toast.warning(`Failed to delete ${result.failed ?? result.failed_count ?? 0} entities`);
        }
      } else {
        toast.error('Bulk delete operation failed');
      }
      setSelectedEntityIds(new Set());
      loadCanonicalEntities();
      loadDashboard();
    } catch (error: any) {
      logger.error('Failed to bulk delete entities:', error);
      toast.error(error.response?.data?.detail || 'Failed to bulk delete entities');
    } finally {
      setBulkDeleting(false);
    }
  };

  // Bulk Retype Handler - opens the type picker dialog
  const handleOpenBulkRetypeModal = () => {
    if (selectedEntityIds.size === 0) return;
    setBulkRetypeTargetType('');
    setBulkRetypeModalVisible(true);
  };

  // Bulk Retype Confirm Handler - calls the API
  const handleBulkRetypeConfirm = async () => {
    if (selectedEntityIds.size === 0 || !bulkRetypeTargetType) return;

    setBulkRetyping(true);
    try {
      const result = await entityQAService.bulkRetypeEntities(
        Array.from(selectedEntityIds),
        bulkRetypeTargetType
      );
      if (result.success) {
        toast.success(`Successfully retyped ${result.updated_count} entities to ${bulkRetypeTargetType}`);
        if (result.errors && result.errors.length > 0) {
          toast.warning(`${result.errors.length} entities failed to retype`);
        }
      } else {
        toast.error('Bulk retype operation failed');
      }
      setBulkRetypeModalVisible(false);
      setSelectedEntityIds(new Set());
      loadCanonicalEntities();
      loadDashboard();
    } catch (error: any) {
      logger.error('Failed to bulk retype entities:', error);
      toast.error(error.response?.data?.detail || 'Failed to bulk retype entities');
    } finally {
      setBulkRetyping(false);
    }
  };

  // AI Analysis Handler - auto-processes all entities in batches of 500
  const handleAIAnalyze = async (autoApply: boolean = false) => {
    setAiAnalyzingMode(autoApply ? 'heal' : 'analyze');

    let offset = 0;
    let batchNum = 0;
    let totalAnalyzed = 0;
    let totalCorrect = 0;
    let totalCorrections = 0;
    let totalHighConfidence = 0;
    let totalTokens = 0;
    let totalCost = 0;
    let totalTime = 0;
    let totalApplied = 0;
    let totalSkippedLowConf = 0;
    let totalApplyErrors = 0;
    const allResults: AIAnalysisResponse['results'] = [];
    const aggregatedFilterStats = {
      total_candidates: 0,
      skipped_high_confidence: 0,
      skipped_pattern_match: 0,
      skipped_majority_vote: 0,
      sent_to_ai: 0,
      cost_saved_usd: 0,
    };
    let lastResult: AIAnalysisResponse | null = null;

    try {
      while (true) {
        batchNum++;
        let result: AIAnalysisResponse;
        if (autoApply) {
          result = await entityQAService.aiAnalyzeAndApply({
            confidenceThreshold: 0.7,
            limit: 500,
            // Match the bar the analysis uses to label a correction "high
            // confidence" so what's reported as high-confidence is what Heal
            // actually applies (see ai_analyze_and_apply_corrections).
            autoApplyThreshold: 0.85,
            offset,
          });
        } else {
          result = await entityQAService.aiAnalyzeEntities({
            confidenceThreshold: 0.7,
            limit: 500,
            offset,
          });
        }

        // Stop on failure
        if (!result.success) {
          lastResult = result;
          break;
        }

        lastResult = result;
        totalAnalyzed += result.total_analyzed;
        totalCorrect += result.correct_classifications;
        totalCorrections += result.suggested_corrections;
        totalHighConfidence += result.high_confidence_corrections;
        totalTokens += result.total_tokens_used;
        totalCost += result.estimated_cost_usd;
        totalTime += result.processing_time_ms;
        totalApplied += result.applied_count ?? 0;
        totalSkippedLowConf += result.skipped_low_confidence ?? 0;
        totalApplyErrors += result.apply_errors?.length ?? 0;
        allResults.push(...(result.results || []));
        if (result.filter_stats) {
          aggregatedFilterStats.total_candidates += result.filter_stats.total_candidates;
          aggregatedFilterStats.skipped_high_confidence += result.filter_stats.skipped_high_confidence;
          aggregatedFilterStats.skipped_pattern_match += result.filter_stats.skipped_pattern_match;
          aggregatedFilterStats.skipped_majority_vote += result.filter_stats.skipped_majority_vote;
          aggregatedFilterStats.sent_to_ai += result.filter_stats.sent_to_ai;
          aggregatedFilterStats.cost_saved_usd += result.filter_stats.cost_saved_usd;
        }

        // No more entities to process
        if (result.next_offset == null || result.next_offset <= offset) {
          break;
        }

        offset = result.next_offset;
      }
    } catch (error) {
      logger.error('AI analysis failed:', error);
      toast.error(`AI analysis failed at batch ${batchNum}${totalAnalyzed > 0 ? ` (${totalAnalyzed} entities processed before error)` : ''}`);
    }

    // Show results (including partial results if error occurred mid-loop)
    if (lastResult && !lastResult.success) {
      toast.error(lastResult.message || 'AI analysis failed');
    } else if (lastResult) {
      lastResult.total_analyzed = totalAnalyzed;
      lastResult.correct_classifications = totalCorrect;
      lastResult.suggested_corrections = totalCorrections;
      lastResult.high_confidence_corrections = totalHighConfidence;
      lastResult.total_tokens_used = totalTokens;
      lastResult.estimated_cost_usd = totalCost;
      lastResult.processing_time_ms = totalTime;
      lastResult.results = allResults;
      lastResult.filter_stats = aggregatedFilterStats;

      setAiResult(lastResult);
      setAiResultVisible(true);

      if (totalAnalyzed === 0) {
        toast.info('All entities are already well-classified - nothing to analyze');
      } else if (autoApply) {
        // Heal mode - report what was actually applied, not just what was found.
        const extras: string[] = [];
        if (totalSkippedLowConf > 0) extras.push(`${totalSkippedLowConf} skipped (low confidence)`);
        if (totalApplyErrors > 0) extras.push(`${totalApplyErrors} failed`);
        const suffix = extras.length ? ` - ${extras.join(', ')}` : '';
        if (totalApplied > 0) {
          toast.success(`Healed ${totalApplied} of ${totalCorrections} corrections${suffix}`);
        } else if (totalCorrections > 0) {
          toast.warning(`Found ${totalCorrections} corrections but applied none${suffix}`);
        } else {
          toast.success(`AI analyzed ${totalAnalyzed} entities - no corrections needed`);
        }
      } else {
        toast.success(`AI analyzed ${totalAnalyzed} entities - ${totalCorrections} corrections found`);
      }

      // Refresh whenever Heal actually changed data, so the table/dashboard
      // reflect the new entity types.
      if (autoApply && totalApplied > 0) {
        loadCanonicalEntities();
        loadDashboard();
      }
    }

    setAiAnalyzingMode(null);
  };

  // Merge Entities Handler
  const handleMergeEntities = async (primaryId: string, secondaryId: string) => {
    try {
      const result = await entityQAService.mergeEntities(primaryId, secondaryId);
      if (result.success) {
        toast.success('Entities merged successfully');
        setSelectedEntityIds(new Set());
        loadCanonicalEntities();
        loadDashboard();
      } else {
        toast.error(result.message || 'Merge failed');
      }
    } catch (error: any) {
      logger.error('Failed to merge entities:', error);
      const serverMsg = error.response?.data?._server_messages;
      let errorText = 'Failed to merge entities';
      if (serverMsg) {
        try {
          const parsed = JSON.parse(serverMsg);
          errorText = typeof parsed === 'string' ? parsed : JSON.parse(parsed[0])?.message || errorText;
        } catch { /* use default */ }
      }
      toast.error(errorText);
    }
  };

  // Merge Cluster Handler
  const handleMergeCluster = async (entityIds: string[], primaryId: string, targetType?: string) => {
    try {
      const result = await entityQAService.mergeCluster(entityIds, primaryId, targetType);
      if (result.failed && result.failed.length > 0) {
        const errorMessages = result.failed.map((f: any) => f.error).join('; ');
        toast.error(`Merge failed: ${errorMessages}`);
      }
      if (result.success && result.merged_count > 0) {
        toast.success(`Merged ${result.merged_count} entities successfully`);
        loadDuplicateClusters();
        loadDashboard();
        loadCanonicalEntities();
      } else if (!result.failed || result.failed.length === 0) {
        toast.error('Merge failed');
      }
    } catch (error: any) {
      logger.error('Failed to merge cluster:', error);
      toast.error(error.response?.data?.detail || error.message || 'Failed to merge cluster');
    }
  };

  // Add Correction Rule Handler
  const handleAddRule = async (rule: {
    name: string;
    from_type: string;
    to_type: string;
    pattern?: string;
    confidence?: number;
    action?: string;
    priority?: number;
  }) => {
    try {
      const result = await entityQAService.addCorrectionRule(rule);
      if (result.success) {
        toast.success('Correction rule added successfully');
        loadCorrectionRules();
      } else {
        toast.error('Failed to add correction rule');
      }
    } catch (error: any) {
      logger.error('Failed to add correction rule:', error);
      toast.error(getFriendlyErrorMessage(error, 'Failed to add correction rule'));
    }
  };

  const handleDeleteRule = async (ruleId: string) => {
    try {
      const result = await entityQAService.deleteCorrectionRule(ruleId);
      if (result.success) {
        toast.success('Correction rule deleted');
        loadCorrectionRules();
      } else {
        toast.error('Failed to delete correction rule');
      }
    } catch (error: any) {
      logger.error('Failed to delete correction rule:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete correction rule');
    }
  };

  const handleToggleRule = async (ruleId: string) => {
    try {
      const result = await entityQAService.toggleCorrectionRule(ruleId);
      if (result.success) {
        toast.success(result.is_active ? 'Rule activated' : 'Rule deactivated');
        loadCorrectionRules();
      } else {
        toast.error('Failed to toggle correction rule');
      }
    } catch (error: any) {
      logger.error('Failed to toggle correction rule:', error);
      toast.error(error.response?.data?.detail || 'Failed to toggle correction rule');
    }
  };

  const handleEditRule = async (ruleId: string, rule: {
    name: string;
    from_type: string;
    to_type: string;
    pattern?: string;
    confidence?: number;
    action?: string;
    priority?: number;
  }) => {
    try {
      const result = await entityQAService.updateCorrectionRule(ruleId, rule);
      if (result.success) {
        toast.success('Correction rule updated');
        loadCorrectionRules();
      } else {
        toast.error('Failed to update correction rule');
      }
    } catch (error: any) {
      logger.error('Failed to update correction rule:', error);
      toast.error(getFriendlyErrorMessage(error, 'Failed to update correction rule'));
    }
  };

  // Rebuild Graph Handler
  const handleRebuildGraph = async () => {
    setRebuildingGraph(true);
    try {
      const result = await entityQAService.rebuildNeo4jGraph();
      setRebuildResult(result);
      setRebuildResultVisible(true);

      if (result.success) {
        toast.success('Knowledge graph rebuilt successfully');
        loadNeo4jStats();
      } else {
        toast.error(result.message || 'Failed to rebuild knowledge graph');
      }
    } catch (error: any) {
      logger.error('Failed to rebuild graph:', error);
      toast.error('Failed to rebuild knowledge graph');
    } finally {
      setRebuildingGraph(false);
    }
  };

  // Review Data Quality Handler
  const handleReviewDataQuality = async () => {
    setScanning(true);
    try {
      const result = await entityQAService.reviewDataQuality();
      if (result.success) {
        const d = result.data;
        const parts = [`${d.invalid_found} invalid entries found`, `${d.issues_created} new issues created`];
        if (d.noise_found) parts.push(`${d.noise_found} noise detected`);
        if (d.duplicates_found) parts.push(`${d.duplicates_found} duplicates found`);
        if (d.auto_merged) parts.push(`${d.auto_merged} auto-merged`);
        toast.success(parts.join('. ') + '.');
        loadIssues();
        loadDashboard();
        loadIssueTypeBreakdown();
      } else {
        toast.error('Data quality review returned no results.');
      }
    } catch (error: any) {
      logger.error('Data quality review failed:', error);
      toast.error('Data quality review failed');
    } finally {
      setScanning(false);
    }
  };

  // Auto-Fix Handler
  const handleAutoFix = async () => {
    setAutoFixing(true);
    try {
      // First do a dry run to preview
      const preview = await entityQAService.autoFixIssues(true);
      if (preview.total_fixed === 0) {
        toast.info('No issues to auto-fix');
        setAutoFixing(false);
        return;
      }
      // Show preview in custom modal
      setAutoFixPreview(preview);
      setAutoFixConfirmVisible(true);
      setAutoFixing(false);
    } catch (error: any) {
      logger.error('Auto-fix preview failed:', error);
      toast.error('Auto-fix preview failed');
      setAutoFixing(false);
    }
  };

  const handleAutoFixConfirm = async () => {
    setAutoFixConfirmVisible(false);
    setAutoFixPreview(null);
    setAutoFixing(true);
    try {
      const result = await entityQAService.autoFixIssues(false);
      if (result.success) {
        if (result.total_fixed === 0) {
          toast.info(result.message || 'No issues needed fixing');
        } else {
          toast.success(result.message);
        }
        if ((result as any).reindex_recommended) {
          toast.info('Some documents may have stale vectors. Check the Vector Health tab to re-index.', {
            duration: 6000,
            action: {
              label: 'Go to Vector Health',
              onClick: () => setActiveTab('vector-health'),
            },
          });
        }
        loadIssues();
        loadDashboard();
        loadIssueTypeBreakdown();
        if (activeTab === 'canonical') loadCanonicalEntities();
      } else {
        toast.error('Auto-fix failed');
      }
    } catch (error: any) {
      logger.error('Auto-fix failed:', error);
      toast.error('Auto-fix failed');
    } finally {
      setAutoFixing(false);
    }
  };

  // Auto-Fix by Type Handler
  const handleAutoFixByType = (issueType: string, action: string) => {
    const message = issueType === 'noise'
      ? 'Delete ALL pending noise entities? This cannot be undone.'
      : 'Auto-merge ALL pending duplicate entities?';
    setAutoFixByTypeConfirm({ issueType, action, message });
  };

  const handleAutoFixByTypeConfirm = async () => {
    if (!autoFixByTypeConfirm) return;
    const { issueType, action } = autoFixByTypeConfirm;
    setAutoFixByTypeConfirm(null);
    setBulkActioning(true);
    try {
      const result = await entityQAService.autoFixByType(issueType, action);
      if (result.success) {
        toast.success(result.message);
        loadIssues();
        loadDashboard();
        loadIssueTypeBreakdown();
        if (activeTab === 'canonical') loadCanonicalEntities();
      } else {
        toast.error(`Failed to auto-fix ${issueType} issues`);
      }
    } catch (error: any) {
      logger.error(`Auto-fix by type failed:`, error);
      toast.error(`Failed to auto-fix ${issueType} issues`);
    } finally {
      setBulkActioning(false);
    }
  };

  // Re-index Handlers
  const handleReindexDocument = async (documentId: string) => {
    setReindexing(true);
    try {
      const result = await entityQAService.batchReindexDocuments([documentId]);
      if (result.async) {
        toast.success(result.message || 'Re-indexing started in background');
      } else if (result.succeeded && result.succeeded > 0) {
        toast.success('Document re-indexed successfully');
      } else {
        toast.error('Re-index failed');
      }
      loadVectorHealth();
    } catch (error: any) {
      logger.error('Failed to re-index document:', error);
      toast.error('Failed to re-index document');
    } finally {
      setReindexing(false);
    }
  };

  const handleBatchReindex = async (documentIds: string[]) => {
    if (documentIds.length === 0) return;
    setReindexing(true);
    try {
      const result = await entityQAService.batchReindexDocuments(documentIds);
      if (result.async) {
        toast.success(result.message || `Re-indexing ${documentIds.length} documents in background`);
      } else {
        const msg = `Re-indexed: ${result.succeeded} succeeded, ${result.failed} failed`;
        if (result.failed && result.failed > 0) {
          toast.warning(msg);
        } else {
          toast.success(msg);
        }
      }
      loadVectorHealth();
    } catch (error: any) {
      logger.error('Failed to batch re-index:', error);
      toast.error('Failed to batch re-index documents');
    } finally {
      setReindexing(false);
    }
  };

  // Row Selection for bulk operations
  const handleSelectEntity = (entityId: string, checked: boolean) => {
    setSelectedEntityIds((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(entityId);
      } else {
        next.delete(entityId);
      }
      return next;
    });
  };

  // Select/deselect all entities on the CURRENT page only, preserving cross-page selections
  const handleSelectAllEntities = (checked: boolean) => {
    const currentPageIds = new Set(canonicalEntities.map((e) => e.id));
    if (checked) {
      setSelectedEntityIds((prev) => {
        const next = new Set(prev);
        currentPageIds.forEach((id) => next.add(id));
        return next;
      });
    } else {
      setSelectedEntityIds((prev) => {
        const next = new Set(prev);
        currentPageIds.forEach((id) => next.delete(id));
        return next;
      });
    }
  };

  // Clear all selections across all pages
  const handleClearAllSelections = useCallback(() => {
    setSelectedEntityIds(new Set());
  }, []);

  // Sort change handler for issues
  const handleIssueSortChange = useCallback((field: string) => {
    if (issueSortBy === field) {
      setIssueSortOrder(o => o === 'asc' ? 'desc' : 'asc');
    } else {
      setIssueSortBy(field);
      setIssueSortOrder('asc');
    }
    setIssuesPagination(p => ({ ...p, current: 1 }));
  }, [issueSortBy]);

  // Sort change handler for canonical entities (clears selections on sort change)
  const handleCanonicalSortChange = useCallback((field: string) => {
    if (canonicalSortBy === field) {
      setCanonicalSortOrder(o => o === 'asc' ? 'desc' : 'asc');
    } else {
      setCanonicalSortBy(field);
      setCanonicalSortOrder('asc');
    }
    setCanonicalPagination(p => ({ ...p, current: 1 }));
    setSelectedEntityIds(new Set());
  }, [canonicalSortBy]);

  // Search change handler for canonical entities (clears selections on search change)
  const handleCanonicalSearchChange = useCallback((search: string) => {
    setCanonicalSearch(search);
    setCanonicalPagination(p => ({ ...p, current: 1 }));
    setSelectedEntityIds(new Set());
  }, []);

  // ============================================================================
  // Computed Values
  // ============================================================================

  const qualityStatus = dashboard ? getQualityScoreStatus(dashboard.quality_score || 0) : { color: 'text-muted-foreground', icon: null };
  const entityBreakdownKeys = safeObjectKeys(dashboard?.entity_breakdown);
  const entityBreakdownEntries = safeObjectEntries(dashboard?.entity_breakdown);

  // Compute similar issues for the resolve modal (same issue_type + entity_type, excluding selected issue)
  const similarIssueIds = useMemo(() => {
    if (!selectedIssue) return [];
    return issues
      .filter(
        (i) =>
          i.id !== selectedIssue.id &&
          i.issue_type === selectedIssue.issue_type &&
          i.entity_type === selectedIssue.entity_type &&
          i.status === 'pending'
      )
      .map((i) => i.id);
  }, [issues, selectedIssue]);

  const similarCount = similarIssueIds.length;

  // Issues pagination change handler - reset to page 1 when filters change
  const handleIssuesPaginationChange = useCallback((newPagination: { current: number; pageSize: number }) => {
    setIssuesPagination(newPagination);
  }, []);

  const handleCanonicalPaginationChange = useCallback((newPagination: { current: number; pageSize: number }) => {
    setCanonicalPagination(newPagination);
  }, []);

  // ============================================================================
  // Render
  // ============================================================================

  return (
    <div className="dark bg-background text-foreground min-h-full space-y-6 max-w-full p-6">
      {/* Header Card */}
      <QAPageHeader
        neo4jStats={neo4jStats}
        aiAnalyzingMode={aiAnalyzingMode}
        rebuildingGraph={rebuildingGraph}
        dashboardLoading={dashboardLoading}
        autoFixing={autoFixing}
        onAIAnalyze={handleAIAnalyze}
        onRebuildGraph={handleRebuildGraph}
        onRefreshAll={handleRefreshAll}
        onAutoFix={handleAutoFix}
      />

      {/* Data Quality Review Button */}
      <div className="flex justify-end">
        <Button
          variant="outline"
          size="sm"
          onClick={handleReviewDataQuality}
          disabled={scanning}
        >
          {scanning ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
          Review Data Quality
        </Button>
      </div>

      {/* Dashboard Error Alert */}
      {dashboardError && (
        <QAErrorAlert
          message="Dashboard Loading Error"
          error={dashboardError}
          onRetry={loadDashboard}
        />
      )}

      {/* Overview Statistics Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-4">
        <QAStatCard
          title="Total Entities"
          value={dashboard?.total_entities ?? 0}
          icon={<Users className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />}
          iconBgClass="bg-primary/15"
          loading={dashboardLoading}
        />
        <QAStatCard
          title="Issues Found"
          value={dashboard?.total_issues ?? 0}
          icon={<AlertCircle className="h-4 w-4 sm:h-5 sm:w-5 text-amber-400" />}
          iconBgClass="bg-amber-500/15"
          loading={dashboardLoading}
          valueColorClass={(dashboard?.total_issues || 0) > 0 ? 'text-amber-400' : ''}
          subtitle={dashboard ? `${dashboard.pending_review || 0} pending | ${dashboard.resolved_count || 0} resolved | ${dashboard.ignored_count || 0} ignored` : undefined}
        />
        <QAStatCard
          title="Auto-Corrected"
          value={dashboard?.auto_corrected ?? 0}
          icon={<Zap className="h-4 w-4 sm:h-5 sm:w-5 text-emerald-400" />}
          iconBgClass="bg-emerald-500/10"
          loading={dashboardLoading}
          valueColorClass="text-emerald-400"
        />
        <QAStatCard
          title="Quality Score Trend (7 Days)"
          value={`${(dashboard?.quality_score ?? 0).toFixed(2)}%`}
          icon={qualityStatus.icon}
          iconBgClass={(dashboard?.quality_score || 0) >= 90 ? 'bg-emerald-500/10' : (dashboard?.quality_score || 0) >= 70 ? 'bg-amber-500/15' : 'bg-destructive/15'}
          loading={dashboardLoading}
          valueColorClass={qualityStatus.color}
          chart={
            metrics && metrics.length > 0 ? (
              <div className="flex items-end gap-1 h-8" aria-hidden="true">
                {metrics.slice(-7).map((m, i) => (
                  <div
                    key={`${m.date}-${i}`}
                    className={`flex-1 rounded-sm min-h-[3px] ${(m.quality_score || 0) >= 90 ? 'bg-emerald-500/50' : (m.quality_score || 0) >= 70 ? 'bg-amber-500/50' : 'bg-destructive/50'}`}
                    style={{ height: `${Math.max(8, Math.min(100, m.quality_score || 0))}%` }}
                    title={`${m.date}: ${(m.quality_score || 0).toFixed(1)}%`}
                  />
                ))}
              </div>
            ) : undefined
          }
        />
      </div>

      {/* Main Content Tabs */}
      <Card>
        <CardContent className="p-3 sm:pt-6 sm:px-6">
          <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as ActiveTab)}>
            <TabsList className="grid w-full grid-cols-6 h-auto">
              <TabsTrigger value="issues" className="min-w-0 gap-1 xl:gap-2 px-1 xl:px-3 py-2 text-xs xl:text-sm">
                <Bug className="h-4 w-4 flex-shrink-0" />
                <span className="hidden xl:inline min-w-0 truncate">Pending Issues</span>
                {(dashboard?.pending_review || 0) > 0 && (
                  <Badge variant="secondary" className="hidden xl:inline-flex ml-1 text-xs px-2">{dashboard?.pending_review}</Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="clusters" className="min-w-0 gap-1 xl:gap-2 px-1 xl:px-3 py-2 text-xs xl:text-sm">
                <GitMerge className="h-4 w-4 flex-shrink-0" />
                <span className="hidden xl:inline min-w-0 truncate">Duplicates</span>
                {clustersTotalCount > 0 && (
                  <Badge variant="secondary" className="hidden xl:inline-flex ml-1 text-xs px-2">{clustersTotalCount}</Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="canonical" className="min-w-0 gap-1 xl:gap-2 px-1 xl:px-3 py-2 text-xs xl:text-sm">
                <CheckCircle2 className="h-4 w-4 flex-shrink-0" />
                <span className="hidden xl:inline min-w-0 truncate">Canonical Entities</span>
                {(dashboard?.total_entities || 0) > 0 && (
                  <Badge variant="secondary" className="hidden xl:inline-flex ml-1 text-xs px-2">{dashboard?.total_entities}</Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="rules" className="min-w-0 gap-1 xl:gap-2 px-1 xl:px-3 py-2 text-xs xl:text-sm">
                <Settings className="h-4 w-4 flex-shrink-0" />
                <span className="hidden xl:inline min-w-0 truncate">Correction Rules</span>
                {(dashboard?.total_correction_rules || 0) > 0 && (
                  <Badge variant="secondary" className="hidden xl:inline-flex ml-1 text-xs px-2">{dashboard?.total_correction_rules}</Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="vector-health" className="min-w-0 gap-1 xl:gap-2 px-1 xl:px-3 py-2 text-xs xl:text-sm">
                <Database className="h-4 w-4 flex-shrink-0" />
                <span className="hidden xl:inline min-w-0 truncate">Vector Health</span>
                {(dashboard?.total_stale_documents || 0) > 0 && (
                  <Badge variant="destructive" className="hidden xl:inline-flex ml-1 text-xs px-2">{dashboard?.total_stale_documents}</Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="settings" className="min-w-0 gap-1 xl:gap-2 px-1 xl:px-3 py-2 text-xs xl:text-sm">
                <Settings className="h-4 w-4 flex-shrink-0" />
                <span className="hidden xl:inline min-w-0 truncate">Settings</span>
              </TabsTrigger>
            </TabsList>

            {/* Issues Tab */}
            <TabsContent value="issues" className="mt-6">
              <QAIssuesTab
                issues={issues}
                totalCount={issuesTotalCount}
                loading={issuesLoading}
                error={issuesError}
                statusFilter={issueStatusFilter}
                severityFilter={issueSeverityFilter}
                pagination={issuesPagination}
                onStatusFilterChange={(val) => { setIssueStatusFilter(val); setIssuesPagination(p => ({ ...p, current: 1 })); }}
                onSeverityFilterChange={(val) => { setIssueSeverityFilter(val); setIssuesPagination(p => ({ ...p, current: 1 })); }}
                onClearFilters={() => {
                  setIssueSearch('');
                  setIssueTypeFilter('all');
                  setIssueStatusFilter('pending');
                  setIssueSeverityFilter('all');
                  setIssuesPagination(p => ({ ...p, current: 1 }));
                }}
                onPaginationChange={handleIssuesPaginationChange}
                onRefresh={async () => {
                  setIssuesRefreshing(true);
                  await loadIssues();
                  setIssuesRefreshing(false);
                }}
                refreshing={issuesRefreshing}
                onResolve={handleOpenResolveModal}
                searchValue={issueSearch}
                onSearchChange={handleIssueSearchChange}
                issueTypeFilter={issueTypeFilter}
                onIssueTypeFilterChange={handleIssueTypeFilterChange}
                issueTypeBreakdown={issueTypeBreakdown}
                selectedIds={selectedIssueIds}
                onSelectIssue={handleSelectIssue}
                onSelectAll={handleSelectAllIssues}
                onBulkDismiss={handleBulkDismiss}
                onBulkDelete={handleBulkDeleteIssues}
                onDismissSingle={handleDismissSingle}
                onDeleteSingle={handleDeleteSingle}
                onAutoFixByType={handleAutoFixByType}
                bulkActioning={bulkActioning}
                sortBy={issueSortBy}
                sortOrder={issueSortOrder}
                onSortChange={handleIssueSortChange}
              />
            </TabsContent>

            {/* Duplicate Clusters Tab */}
            <TabsContent value="clusters" className="mt-4 sm:mt-6">
              <QADuplicateClustersTab
                clusters={clusters}
                totalClusters={clustersTotalCount}
                totalDuplicateEntities={clustersDuplicateCount}
                loading={clustersLoading}
                error={clustersError}
                validEntityTypes={validEntityTypes}
                onMergeCluster={handleMergeCluster}
                onRefresh={loadDuplicateClusters}
              />
            </TabsContent>

            {/* Canonical Entities Tab */}
            <TabsContent value="canonical" className="mt-4 sm:mt-6">
              <QACanonicalTab
                entities={canonicalEntities}
                totalCount={canonicalTotalCount}
                loading={canonicalLoading}
                error={canonicalError}
                typeFilter={canonicalTypeFilter}
                searchValue={canonicalSearch}
                entityTypeOptions={entityBreakdownKeys}
                entityBreakdownEntries={entityBreakdownEntries}
                pagination={canonicalPagination}
                selectedIds={selectedEntityIds}
                bulkDeleting={bulkDeleting}
                sortBy={canonicalSortBy}
                sortOrder={canonicalSortOrder}
                onSortChange={handleCanonicalSortChange}
                onTypeFilterChange={(val) => { setCanonicalTypeFilter(val); setCanonicalPagination(p => ({ ...p, current: 1 })); setSelectedEntityIds(new Set()); }}
                onSearchChange={handleCanonicalSearchChange}
                onPaginationChange={handleCanonicalPaginationChange}
                onRefresh={loadCanonicalEntities}
                onEdit={handleOpenEditModal}
                onDelete={handleDeleteEntity}
                onBulkDelete={handleBulkDelete}
                onBulkRetype={handleOpenBulkRetypeModal}
                onMerge={handleMergeEntities}
                onSelectEntity={handleSelectEntity}
                onSelectAll={handleSelectAllEntities}
                onClearAllSelections={handleClearAllSelections}
              />
            </TabsContent>

            {/* Correction Rules Tab */}
            <TabsContent value="rules" className="mt-4 sm:mt-6">
              <QARulesTab
                rules={correctionRules}
                loading={rulesLoading}
                error={rulesError}
                onRefresh={loadCorrectionRules}
                onAddRule={handleAddRule}
                onDeleteRule={handleDeleteRule}
                onToggleRule={handleToggleRule}
                onEditRule={handleEditRule}
              />
            </TabsContent>

            {/* Vector Health Tab */}
            <TabsContent value="vector-health" className="mt-4 sm:mt-6">
              <QAVectorHealthTab
                audit={vectorHealthAudit}
                staleData={vectorStaleData}
                loading={vectorHealthLoading}
                error={vectorHealthError}
                reindexing={reindexing}
                onRefresh={loadVectorHealth}
                onReindexDocument={handleReindexDocument}
                onBatchReindex={handleBatchReindex}
              />
            </TabsContent>

            {/* Settings Tab */}
            <TabsContent value="settings" className="mt-4 sm:mt-6">
              <QASettingsTab />
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Resolve Issue Modal */}
      <QAResolveModal
        open={resolveModalVisible}
        onOpenChange={setResolveModalVisible}
        issue={selectedIssue}
        action={resolveAction}
        notes={resolveNotes}
        resolving={resolving}
        onActionChange={setResolveAction}
        onNotesChange={setResolveNotes}
        onResolve={handleResolveIssue}
        validEntityTypes={validEntityTypes}
        correctedType={resolveCorrectedType}
        onCorrectedTypeChange={setResolveCorrectedType}
        applyToSimilar={applyToSimilar}
        onApplyToSimilarChange={setApplyToSimilar}
        similarCount={similarCount}
      />

      {/* Edit Entity Modal */}
      <QAEditEntityModal
        open={editModalVisible}
        onOpenChange={setEditModalVisible}
        entity={selectedEntity}
        entityType={editEntityType}
        canonicalValue={editCanonicalValue}
        confidenceScore={editConfidenceScore}
        notes={editNotes}
        saving={editSaving}
        onEntityTypeChange={setEditEntityType}
        onCanonicalValueChange={setEditCanonicalValue}
        onConfidenceScoreChange={setEditConfidenceScore}
        onNotesChange={setEditNotes}
        onSave={handleSaveEntity}
      />

      {/* Rebuild Graph Result Modal */}
      <QARebuildResultModal
        open={rebuildResultVisible}
        onOpenChange={setRebuildResultVisible}
        result={rebuildResult}
      />

      {/* AI Analysis Results Modal */}
      <QAAIAnalysisModal
        open={aiResultVisible}
        onOpenChange={setAiResultVisible}
        result={aiResult}
      />

      {/* Bulk Retype Modal */}
      <Dialog open={bulkRetypeModalVisible} onOpenChange={setBulkRetypeModalVisible}>
        <DialogContent className="dark bg-background text-foreground max-w-[90vw] sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Change Entity Type</DialogTitle>
            <DialogDescription>
              Change the type of <strong>{selectedEntityIds.size}</strong> selected entities to a new type.
              This will update the canonical entity records, linked document entities, and propagate the change to Neo4j.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <label className="text-sm font-medium mb-2 block">New Entity Type</label>
            <Select value={bulkRetypeTargetType} onValueChange={setBulkRetypeTargetType}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select a type..." />
              </SelectTrigger>
              <SelectContent className="dark">
                {validEntityTypes.map((type) => (
                  <SelectItem key={type} value={type}>{type}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkRetypeModalVisible(false)} disabled={bulkRetyping}>
              Cancel
            </Button>
            <Button onClick={handleBulkRetypeConfirm} disabled={!bulkRetypeTargetType || bulkRetyping}>
              {bulkRetyping ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Change Type
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Auto-Fix Preview Confirmation Dialog */}
      <Dialog open={autoFixConfirmVisible} onOpenChange={(open) => {
        if (!open) { setAutoFixConfirmVisible(false); setAutoFixPreview(null); }
      }}>
        <DialogContent className="dark bg-background text-foreground max-w-[90vw] sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Auto-Fix Preview</DialogTitle>
            <DialogDescription>
              The following issues will be automatically fixed:
            </DialogDescription>
          </DialogHeader>
          {autoFixPreview && (
            <div className="space-y-3 py-2">
              <div className="flex justify-between items-center px-3 py-2 bg-destructive/10 rounded-md">
                <span className="text-sm">Noise entities to delete</span>
                <span className="font-semibold text-sm">{autoFixPreview.noise_deleted}</span>
              </div>
              <div className="flex justify-between items-center px-3 py-2 bg-amber-500/10 rounded-md">
                <span className="text-sm">Duplicates to auto-merge</span>
                <span className="font-semibold text-sm">{autoFixPreview.duplicates_merged}</span>
              </div>
              {(autoFixPreview as any).duplicates_review > 0 && (
                <div className="flex justify-between items-center px-3 py-2 bg-amber-500/10 rounded-md">
                  <span className="text-sm text-amber-400">Duplicates needing manual review</span>
                  <span className="font-semibold text-sm text-amber-400">{(autoFixPreview as any).duplicates_review}</span>
                </div>
              )}
              <div className="flex justify-between items-center px-3 py-2 bg-secondary rounded-md">
                <span className="text-sm">Orphans to dismiss</span>
                <span className="font-semibold text-sm">{autoFixPreview.orphans_dismissed}</span>
              </div>
              <div className="flex justify-between items-center px-3 py-2 bg-primary/10 rounded-md font-medium">
                <span className="text-sm">Total fixes</span>
                <span className="font-bold text-sm">{autoFixPreview.total_fixed}</span>
              </div>
            </div>
          )}
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => { setAutoFixConfirmVisible(false); setAutoFixPreview(null); }}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleAutoFixConfirm}>
              Apply Fixes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Auto-Fix by Type Confirmation Dialog */}
      <Dialog open={!!autoFixByTypeConfirm} onOpenChange={(open) => {
        if (!open) setAutoFixByTypeConfirm(null);
      }}>
        <DialogContent className="dark bg-background text-foreground max-w-[90vw] sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Confirm Auto-Fix</DialogTitle>
            <DialogDescription>
              {autoFixByTypeConfirm?.message}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setAutoFixByTypeConfirm(null)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleAutoFixByTypeConfirm}>
              Proceed
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default KBEntityQAPage;
